import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageLoader } from "@/components/common/loader";
import { getPortalIncomeExpense, patchPortalIncomeExpense } from "@/features/portal/api";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import { FIELD_LABEL } from "./fields";

/**
 * The limited-field income/expense grid. Every money field is integer cents on
 * the wire; we display dollars and autosave on blur. Deliberately **no**
 * proposed/OIC columns, offer amount, or IA schedule — the portal only captures
 * actuals (spec §9 "Limited-field 433 = no calc").
 */

interface FieldDef {
  key: string;
  label: string;
}

// Monthly income lines (Tier 2.2 IncomeExpense model field names).
const INCOME_FIELDS: FieldDef[] = [
  { key: "ss_primary", label: "Social Security (you)" },
  { key: "ss_spouse", label: "Social Security (spouse)" },
  { key: "pension_primary", label: "Pension (you)" },
  { key: "pension_spouse", label: "Pension (spouse)" },
  { key: "additional_inc", label: "Additional income" },
  { key: "interest", label: "Interest income" },
  { key: "dividends", label: "Dividends" },
  { key: "rental_inc", label: "Net rental income" },
  { key: "distributions", label: "Distributions (K-1, etc.)" },
  { key: "support_inc", label: "Child support received" },
  { key: "alimony_inc", label: "Alimony received" },
  { key: "unemployment_inc", label: "Unemployment (you)" },
  { key: "unemp_spouse_inc", label: "Unemployment (spouse)" },
  { key: "other1_inc", label: "Other income" },
];

// Monthly actual-expense lines — the `_exp` (actual) columns only. The `_prop`
// (IRS-allowed / proposed) and `_oic` columns are practitioner-only (spec §9).
const EXPENSE_FIELDS: FieldDef[] = [
  { key: "food_exp", label: "Food" },
  { key: "housekeep_exp", label: "Housekeeping supplies" },
  { key: "clothes_exp", label: "Clothing" },
  { key: "personal_exp", label: "Personal care" },
  { key: "misc_exp", label: "Miscellaneous" },
  { key: "rent_exp", label: "Rent" },
  { key: "dwp_exp", label: "Utilities (gas/electric/water)" },
  { key: "phone_exp", label: "Phone / cell / internet" },
  { key: "re_tax_exp", label: "Real estate taxes" },
  { key: "re_repair_exp", label: "Home maintenance / repairs" },
  { key: "vehicle_exp", label: "Vehicle payment" },
  { key: "vehicle2_exp", label: "Second vehicle payment" },
  { key: "operating_exp", label: "Vehicle operating costs" },
  { key: "pubtrans_exp", label: "Public transportation" },
  { key: "ret_req_exp", label: "Required retirement contributions" },
  { key: "ret_vol_exp", label: "Voluntary retirement contributions" },
  { key: "court_exp", label: "Court-ordered payments" },
  { key: "court_child_exp", label: "Court-ordered child support" },
  { key: "court_alim_exp", label: "Court-ordered alimony" },
  { key: "health_ins_exp", label: "Health insurance" },
  { key: "health_exp", label: "Out-of-pocket health care" },
  { key: "child_exp", label: "Child / dependent care" },
  { key: "life_ins_exp", label: "Term life insurance" },
  { key: "union_exp", label: "Union dues" },
  { key: "student_exp", label: "Student loans" },
  { key: "est_tax_exp", label: "Estimated tax payments" },
  { key: "tax_exp", label: "Current year taxes" },
  { key: "secure_exp", label: "Other secured debts" },
  { key: "delinq_tax_exp", label: "Delinquent state/local taxes" },
  { key: "legal_exp", label: "Legal fees" },
  { key: "tax_prep_exp", label: "Tax preparation" },
  { key: "tax_res_exp", label: "Tax resolution fees" },
  { key: "charity_exp", label: "Charitable contributions" },
  { key: "education_exp", label: "Education" },
  { key: "involuntary_exp", label: "Involuntary deductions" },
  { key: "credit_card_exp", label: "Credit card payments" },
  { key: "other_exp", label: "Other expenses" },
  { key: "other_with_exp", label: "Other withholding" },
];

/** A single money cell with local state; autosaves on blur when changed. */
function MoneyCell({
  label,
  cents,
  onCommit,
}: {
  label: string;
  cents: number | null;
  onCommit: (cents: number) => Promise<void>;
}) {
  const [local, setLocal] = useState(cents == null ? "" : centsToDollars(cents));
  const [saving, setSaving] = useState(false);
  const original = cents == null ? "" : centsToDollars(cents);

  // Keep the field in sync if the server value changes (e.g. reload).
  useEffect(() => {
    setLocal(cents == null ? "" : centsToDollars(cents));
  }, [cents]);

  async function commit() {
    if (local === original) return;
    setSaving(true);
    try {
      await onCommit(dollarsToCents(local));
    } catch {
      toast.error("Couldn't save — try again.");
      setLocal(original);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{label}</Label>
      <div className="relative">
        <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm">
          $
        </span>
        <Input
          className="pl-7"
          value={local}
          inputMode="decimal"
          placeholder="0.00"
          disabled={saving}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) setLocal(raw);
          }}
          onBlur={commit}
        />
      </div>
    </div>
  );
}

function toCents(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function IncomeExpenseTab() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getPortalIncomeExpense()
      .then((d) => active && setData(d))
      .catch(() => active && setData({}))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  async function commit(field: string, cents: number) {
    const updated = await patchPortalIncomeExpense({ [field]: cents });
    // Merge whatever the server echoes back so dependent totals stay fresh.
    setData((prev) => ({ ...(prev ?? {}), ...(updated ?? {}), [field]: cents }));
  }

  if (loading) return <PageLoader />;
  const d = data ?? {};

  return (
    <div className="grid gap-6">
      <p className="text-muted-foreground text-sm">
        Enter your <span className="font-medium">monthly</span> income and actual expenses. Your
        practitioner reviews these — no calculations happen here.
      </p>

      <Card className="grid gap-5 p-4">
        <h3 className="font-semibold">Monthly income</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INCOME_FIELDS.map((f) => (
            <MoneyCell
              key={f.key}
              label={f.label}
              cents={toCents(d[f.key])}
              onCommit={(cents) => commit(f.key, cents)}
            />
          ))}
        </div>
      </Card>

      <Card className="grid gap-5 p-4">
        <h3 className="font-semibold">Monthly actual expenses</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {EXPENSE_FIELDS.map((f) => (
            <MoneyCell
              key={f.key}
              label={f.label}
              cents={toCents(d[f.key])}
              onCommit={(cents) => commit(f.key, cents)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}
